import { LogLevel } from '@digital-bridge/artisan';
import { AryaAdaptor, EnvironmentMapOptions } from '@digital-bridge/artisan-arya';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import * as O from 'fp-ts/Option';
import * as Hooks from 'preact/hooks';
import { getLogger } from '../../store/middleware/log';
import * as Selectors from '../../store/selectors';
import * as AryaInterface from '../arya/interface';
import { useSelector } from './redux';

axiosRetry(axios, {
    // Given the lack of actual handling of request errors during load,
    // we want to continue retrying long beyond any reasonable number
    // of attempts - exponential backoff ensures we won't actually
    // make a large number of requests over any duration the user is likely
    // to wait.
    // TODO: This should be reduced once we can reasonably handle any
    // error conditions.
    retries: 16,
    retryDelay: axiosRetry.exponentialDelay,
});

/**
 * Initialize an instance of Arya against HTMLCanvasElements.
 * Only loads a single model, no matter the furniture.
 */
export const useArya = (
    canvas3d: React.MutableRefObject<HTMLCanvasElement>,
    canvas2d: React.MutableRefObject<HTMLCanvasElement>,
    showDebugTools: boolean,
    environmentMap: EnvironmentMapOptions | undefined,
): O.Option<AryaInterface.Interface> => {
    const [arya, setArya] = Hooks.useState(null as null | AryaAdaptor);

    const modelsUrl = useSelector(Selectors.Core.modelsUrl);
    const tenantIdentifier = useSelector(Selectors.Core.tenantIdentifier);
    const isIOS = useSelector(Selectors.Core.isIOS);
    const isSafari = useSelector(Selectors.Core.isSafari);

    Hooks.useEffect(() => {
        let _arya: AryaAdaptor | undefined = undefined;

        const c2d = canvas2d.current;
        const c3d = canvas3d.current;

        if (c2d === null || c3d === null) {
            return () => {};
        }

        // TODO: remove this when safari WebGL2 isn't broken!
        const forceWebGL1 = isIOS || isSafari;

        _arya = new AryaAdaptor(
            {
                retrieveModel: async (id: string) => {
                    const modelBlob = await axios.get(
                        `${modelsUrl}/v1/${tenantIdentifier}/${id}.glb`,
                        {
                            responseType: 'blob',
                        },
                    );
                    const modelData = (
                        await axios.get(`${modelsUrl}/v1/${tenantIdentifier}/${id}.json`, {
                            responseType: 'json',
                        })
                    ).data;
                    const modelFile = await (modelBlob.data as Blob).arrayBuffer();
                    return {
                        canonicalId: id,
                        parameters: modelData,
                        modelFile,
                    };
                },
                retrieveCovering: async (id: string) => {
                    const coveringBlob = await axios.get(`assets/coverings/${id}.glb`, {
                        responseType: 'blob',
                    });
                    const modelFile = await (coveringBlob.data as Blob).arrayBuffer();
                    const coveringParamData = (
                        await axios.get(`assets/coverings/${id}.json`, {
                            responseType: 'json',
                        })
                    ).data;
                    return {
                        canonicalId: id,
                        parameters: coveringParamData,
                        modelFile,
                    };
                },
            },
            c3d,
            c2d,
            {
                assetPaths: {
                    rotationHandles: 'assets/rotationHandles/rothandles-large.png',
                    rotationHandlesSmall: 'assets/rotationHandles/rothandles-small.png',
                },
                themingOptions: {
                    placeholder: {
                        backgroundColour: '#000000',
                        foregroundColour: '#FFFFFF',
                    },
                    lightingMode: 'outdoor',
                },
                enableFurniturePlaceholders: false,
                logLevel: LogLevel.warning,
                topDownViewConfig: {
                    wallThickness: 0.025,
                    font: {
                        size: 16,
                        family: 'Arial',
                        colour: '#FF00FF',
                    },
                    box: { padding: 10, colour: 'black', lineWidth: 1 },
                    unit: {
                        fromMeters: (meters: number) => `${meters} m`,
                    },
                },
                dynamicResolutionScaling: true,
                roomFeatures2D: false,
                initialViewMode: '3D',
                obnoxiousFailure: true,
                logarithmicDepthBuffer: true,
                showDebugRenderInfo: showDebugTools,
                enableSky: false,
                useRenderRequiredFlag: true,
                showDebugGui: showDebugTools,
                showProductDistances: true,
                enableCollisionDetection: false,
                enableClampToCeiling: false,
                enableRegionSelection: false,
                environmentMap,
                forceWebGL1,
            },
            getLogger(),
        );
        setArya(_arya);

        return () => {
            _arya?.shutdown();
        };
    }, [canvas3d, canvas2d, modelsUrl]);

    return AryaInterface.build(arya);
};
